import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge, Select, Spinner, Tabs, useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, PageWrapper } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
import { ReportsTable } from '../../components/reports_list/reports_table.component';
import { useCubeApi } from '../../hooks/use_cube_api';
import { useReportInfraStatus } from '../../hooks/use_report_infra_status';
import { useVersionedReports } from '../../hooks/use_versioned_reports';
const filterRosettaReportsByUserRoles = (reports, userRoles) => {
    if (!reports?.length) {
        return [];
    }
    if (userRoles === undefined) {
        return [...reports];
    }
    return reports.filter((report) => {
        if (!report.roles?.length) {
            return true;
        }
        return report.roles.some((role) => userRoles.includes(role));
    });
};
const ReportsListPage = ({ studyId: studyIdProp, rosettaReports = [], hideEnvironmentTabs, reportBasePath, userRoles, }) => {
    const translate = useLocalizeMessage();
    const { studyId: paramStudyId } = useParams();
    const studyId = studyIdProp || paramStudyId;
    const webClient = useApiClient(ApiClientService.WEB);
    const { activeReportsTab, setActiveReportsTab, activeStudyConfigVersion, libRequestsParams, selectedTestEnv, setSelectedTestEnv, isLoadingEnvironments, hasProductionEnvironments, setStudyId, testEnvironmentsOptions, devEnvironments, } = useContext(DataReportsContext);
    // Only ephemeral orgs need the new-infra gate on report links — test (non-ephemeral) and prod
    // orgs' reports are always clickable regardless of their cube config's data_source.
    const isEphemeralOrg = activeReportsTab === 0 &&
        (devEnvironments?.some((env) => env.cortexId === selectedTestEnv.value && env.deployment.type === 'ephemeral') ??
            false);
    const { reports: matchedVersionRosettaReports, isLoading: isLoadingMatchedVersionRosetta, studyConfig: matchedStudyConfigForVersion, } = useVersionedReports(studyId, activeStudyConfigVersion);
    /** Rosetta rows for merge: pinned snapshot vs caller-provided (e.g. draft). */
    const rawRosettaSource = useMemo(() => {
        if (activeStudyConfigVersion === null || !studyId) {
            return rosettaReports;
        }
        if (isLoadingMatchedVersionRosetta) {
            return [];
        }
        if (matchedStudyConfigForVersion) {
            return matchedVersionRosettaReports;
        }
        // Version is pinned but no matching config found — do not fall back to draft
        return [];
    }, [
        activeStudyConfigVersion,
        studyId,
        isLoadingMatchedVersionRosetta,
        matchedStudyConfigForVersion,
        matchedVersionRosettaReports,
        rosettaReports,
    ]);
    const effectiveRosettaReports = useMemo(() => filterRosettaReportsByUserRoles(rawRosettaSource, userRoles), [rawRosettaSource, userRoles]);
    const { cubeApi } = useCubeApi();
    useEffect(() => {
        if (setStudyId) {
            setStudyId(studyId);
        }
    }, [studyId]);
    useEffect(() => {
        if (!selectedTestEnv.value && testEnvironmentsOptions && testEnvironmentsOptions.length > 0) {
            setSelectedTestEnv(testEnvironmentsOptions[0]);
        }
    }, [testEnvironmentsOptions, selectedTestEnv.value, setSelectedTestEnv]);
    // Warm up Cube.js data models by calling /meta endpoint
    // This pre-compiles the data models to avoid cold start delays when viewing reports
    // Cache expires after 5 minutes to match Cube.js server-side cache TTL
    useEffect(() => {
        const CACHE_TTL_MS = 5 * 60 * 1000;
        const STORAGE_KEY = 'cube_meta_warmup';
        const isTestTab = activeReportsTab === 0;
        const hasStableValues = isTestTab ? !!selectedTestEnv.value : true;
        if (!hasStableValues || !cubeApi) {
            return;
        }
        const warmupKey = `${studyId}-${activeReportsTab}-${selectedTestEnv.value || 'prod'}`;
        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                const { key, timestamp } = JSON.parse(cached);
                const isExpired = Date.now() - timestamp > CACHE_TTL_MS;
                if (key === warmupKey && !isExpired) {
                    return;
                }
            }
            catch {
                // Invalid cache, continue with warm-up
            }
        }
        cubeApi
            .meta()
            .then(() => {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ key: warmupKey, timestamp: Date.now() }));
        })
            .catch(() => {
            // Silently ignore errors - this is just a warm-up optimization
        });
    }, [cubeApi, studyId, activeReportsTab, selectedTestEnv.value]);
    const deduplicateByKey = (dashboards) => {
        const latestByKey = new Map();
        for (const dashboard of dashboards) {
            const existing = latestByKey.get(dashboard.key);
            if (!existing || dashboard.version > existing.version) {
                latestByKey.set(dashboard.key, dashboard);
            }
        }
        return Array.from(latestByKey.values());
    };
    const getReports = async () => {
        const reportsRequestParams = activeReportsTab === 0 ? {} : libRequestsParams;
        const { data: response } = await webClient.post('/api/data_reporting/dashboards', { studyId, ...reportsRequestParams });
        return activeReportsTab === 0 ? deduplicateByKey(response.data) : response.data;
    };
    const { data: apiReports, isLoading } = useQuery({
        queryKey: ['data_reports', studyId, 'reports_list', activeReportsTab],
        queryFn: getReports,
    });
    const isResolvingPinnedVersion = !!studyId && activeStudyConfigVersion !== null && isLoadingMatchedVersionRosetta;
    const rosettaIdByKey = useMemo(() => new Map(effectiveRosettaReports.map((r) => [r.libraryResourceKey, r.libraryResourceId])), [effectiveRosettaReports]);
    const normalizedRosettaReports = useMemo(() => effectiveRosettaReports
        .filter((report) => !!report.libraryResourceKey)
        .map((report) => ({
        key: report.libraryResourceKey,
        studyConfigId: report.libraryResourceId,
        data: {
            name: report.name,
            description: report.description,
            cubeConfigId: report.cubeConfigDashboard?.libraryResourceId,
        },
    })), [effectiveRosettaReports]);
    const mergedReports = useMemo(() => {
        const apiReportKeys = new Set((apiReports || []).map((r) => r.key));
        // API reports (from data_reporting) are always shown without version-pinning filters —
        // the data_reporting backend applies its own visibility rules.
        // Rosetta-only reports (not already in the API response) are appended.
        const rosettaOnlyReports = normalizedRosettaReports.filter((r) => !apiReportKeys.has(r.key));
        // Annotate API reports with the study config ID when a matching Rosetta entry exists,
        // so the table link can target the specific version assigned in the study config.
        const annotatedApiReports = (apiReports || []).map((r) => {
            const studyConfigId = rosettaIdByKey.get(r.key);
            return studyConfigId ? { ...r, studyConfigId } : r;
        });
        return [...annotatedApiReports, ...rosettaOnlyReports];
    }, [apiReports, normalizedRosettaReports, rosettaIdByKey]);
    // Only fetch/apply the new-infra gate for ephemeral orgs — test and prod reports stay clickable
    // regardless of cube config data_source, so skip the batched Library lookup entirely otherwise.
    const { newInfraIds } = useReportInfraStatus(isEphemeralOrg ? mergedReports.map((r) => r.data?.cubeConfigId) : []);
    // For ephemeral orgs, a report is only clickable once its CUBE_CONFIG resource is confirmed to
    // be on the new (starrocks) infra — older reports without one 404/network-error when opened, so
    // they render as plain (non-clickable) text instead (see reports_table.component). Test/prod
    // reports are always clickable.
    const data = useMemo(() => mergedReports.map((r) => ({
        ...r,
        isNewInfra: !isEphemeralOrg || (!!r.data?.cubeConfigId && newInfraIds.has(r.data.cubeConfigId)),
    })), [mergedReports, newInfraIds, isEphemeralOrg]);
    const isTableLoading = isLoading || isResolvingPinnedVersion;
    const versionBadge = activeStudyConfigVersion !== null ? (_jsx(Badge, { variant: 'common', label: translate('Build v{version}', { version: String(activeStudyConfigVersion) }) })) : null;
    const TestEnvsTab = useMemo(() => (_jsx(ReportsTable, { studyId: studyId, data: data, isLoading: isTableLoading, reportBasePath: reportBasePath, customHeaderContent: _jsx("div", { className: 'flex-1 flex justify-between items-end gap-4', children: _jsxs("div", { className: 'flex gap-4 items-end', children: [_jsx(Select, { label: translate('Test environment'), options: testEnvironmentsOptions ?? [], value: selectedTestEnv, onChange: (value) => setSelectedTestEnv(value), disabled: isLoadingEnvironments, width: 'sm' }), versionBadge && _jsx("div", { className: 'pb-2', children: versionBadge })] }) }) })), [
        testEnvironmentsOptions,
        selectedTestEnv,
        isLoadingEnvironments,
        data,
        isTableLoading,
        studyId,
        translate,
        reportBasePath,
        versionBadge,
    ]);
    const ProductionTab = useMemo(() => (_jsx(ReportsTable, { studyId: studyId, data: data, isLoading: isTableLoading, reportBasePath: reportBasePath, customHeaderContent: versionBadge ? _jsx("div", { className: 'pb-2', children: versionBadge }) : undefined })), [data, isTableLoading, studyId, reportBasePath, versionBadge]);
    const tabItems = useMemo(() => [
        {
            content: ProductionTab,
            label: translate('Production'),
        },
        {
            content: TestEnvsTab,
            label: translate('Test'),
        },
    ], [ProductionTab, TestEnvsTab, translate]);
    const visualToLogical = (visualIndex) => (visualIndex === 0 ? 1 : 0);
    const logicalToVisual = (logicalIndex) => (logicalIndex === 1 ? 0 : 1);
    if (hideEnvironmentTabs) {
        return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { title: translate('Reports'), subtitle: translate('Monitor your study through interactive reporting.'), LinkComponent: Link }), _jsx(ReportsTable, { studyId: studyId, data: data, isLoading: isTableLoading, reportBasePath: reportBasePath })] }));
    }
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { title: translate('Reports'), subtitle: translate('Monitor your study through interactive reporting.'), LinkComponent: Link }), isLoadingEnvironments ? (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, { size: 'xl' }) })) : (_jsx(_Fragment, { children: hasProductionEnvironments ? (_jsx(Tabs, { tabItems: tabItems, selectedIndex: logicalToVisual(activeReportsTab), onTabChange: (visualIndex) => setActiveReportsTab(visualToLogical(visualIndex)) })) : (TestEnvsTab) }))] }));
};
export default ReportsListPage;
